import { useState } from 'react';
import {
  EditButton,
  RecordContextProvider,
  useGetOne,
} from 'react-admin';
import { Drawer, IconButton, useMediaQuery, Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Menu as MenuLucide } from 'lucide-react';
import { Funeral } from '../types';
import { FuneralShowRedesign } from './FuneralShowRedesign';
import { FuneralShowSidebar } from './FuneralShowSidebar';
import { FuneralOwnerSelector } from './FuneralOwnerSelector';
import { MessageDraftProvider } from './contexts/MessageDraftContext';

function DrawerSkeleton() {
  return (
    <div className="flex flex-col h-full animate-pulse">
      <div className="h-12 bg-muted/30 border-b" />
      <div className="flex-1 p-6 space-y-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-12 bg-muted rounded-lg w-3/4" />
        ))}
      </div>
    </div>
  );
}

export function FuneralDrawer({
  funeralId,
  open,
  onClose,
}: {
  funeralId: number | string | null;
  open: boolean;
  onClose: () => void;
}) {
  const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { data: funeral, isLoading } = useGetOne<Funeral>(
    'funerals',
    { id: funeralId! },
    { enabled: !!funeralId },
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      disableEnforceFocus
      sx={{
        '& .MuiDrawer-paper': {
          // Stay clear of the inbox. `--inbox-dock-w` is how much of the
          // screen the dock has taken (0 when it is closed or floating), so
          // the drawer both shifts left by it and gives back the same width
          // instead of running off the edge. Set here rather than as a global
          // CSS override so it carries `sx` specificity and can't be lost.
          width: isMobile
            ? '100vw'
            : 'min(calc(95vw - var(--inbox-dock-w, 0px)), 1265px)',
          right: isMobile ? 0 : 'var(--inbox-dock-w, 0px)',
        },
      }}
    >
      {isLoading || !funeral ? (
        <DrawerSkeleton />
      ) : (
        <MessageDraftProvider>
          <RecordContextProvider value={funeral}>
            <div className="cleo-ui flex flex-col h-full">
              <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30 shrink-0">
                <span className="text-sm font-medium text-muted-foreground">Funeral Details</span>
                <div className="flex items-center gap-2">
                  <FuneralOwnerSelector />
                  <EditButton label={isMobile ? '' : 'Edit'} />
                  {isMobile && (
                    <IconButton
                      size="small"
                      onClick={() => setSidebarOpen(true)}
                      title="Show details"
                    >
                      <MenuLucide className="h-4 w-4" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    href={`/funerals/${funeralId}/show`}
                    target="_blank"
                    title="Open funeral in new tab"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={onClose} title="Close">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
              <div className="flex flex-1 min-h-0">
                <div className="flex-1 min-w-0 overflow-hidden">
                  <FuneralShowRedesign />
                </div>
                {!isMobile && (
                  <div className="shrink-0 border-l overflow-y-auto">
                    <FuneralShowSidebar />
                  </div>
                )}
              </div>
              {isMobile && (
                <Drawer
                  anchor="right"
                  open={sidebarOpen}
                  onClose={() => setSidebarOpen(false)}
                  disableEnforceFocus
                  PaperProps={{ sx: { width: '85vw', maxWidth: 360 } }}
                >
                  <div className="cleo-ui h-full overflow-y-auto">
                    <FuneralShowSidebar />
                  </div>
                </Drawer>
              )}
            </div>
          </RecordContextProvider>
        </MessageDraftProvider>
      )}
    </Drawer>
  );
}
